import { Controller } from "@hotwired/stimulus"

// Decision tree definition
const DECISION_TREE = {
  start: {
    id: "start",
    message: "Hi! I'll help you find the right roofer. What brings you here today?",
    type: "options",
    field: "intent",
    options: [
      { label: "Roof repair (storm/leak/damage)", value: "repair", next: "roofing_type" },
      { label: "Roof replacement", value: "replacement", next: "roofing_type" },
      { label: "Insurance claim help", value: "insurance_claim", next: "insurance_detail" },
      { label: "Free inspection / maintenance", value: "inspection", next: "roofing_type" },
      { label: "Just browsing", value: "browsing", next: "location" }
    ]
  },
  roofing_type: {
    id: "roofing_type",
    message: "What type of roofing do you have?",
    type: "options",
    field: "roofing_type",
    options: [
      { label: "Asphalt shingles", value: "asphalt_shingles", next: "location" },
      { label: "Metal", value: "metal", next: "location" },
      { label: "Tile", value: "tile", next: "location" },
      { label: "Flat", value: "flat", next: "location" },
      { label: "Not sure", value: "not_sure", next: "location" }
    ]
  },
  insurance_detail: {
    id: "insurance_detail",
    message: "Has your insurance company already assigned an adjuster?",
    type: "options",
    field: "insurance_detail",
    options: [
      { label: "Yes", value: "yes", next: "location" },
      { label: "No", value: "no", next: "location" },
      { label: "Not sure", value: "not_sure", next: "location" }
    ]
  },
  location: {
    id: "location",
    message: "Where's your property located?",
    type: "text",
    field: "location",
    placeholder: "City, zip code, or state",
    next: "urgency"
  },
  urgency: {
    id: "urgency",
    message: "How soon do you need this done?",
    type: "options",
    field: "urgency",
    options: [
      { label: "ASAP (emergency)", value: "asap", next: "email_gate" },
      { label: "Within 2 weeks", value: "two_weeks", next: "email_gate" },
      { label: "Within a month", value: "one_month", next: "email_gate" },
      { label: "Just planning ahead", value: "planning", next: "email_gate" }
    ]
  },
  email_gate: {
    id: "email_gate",
    message: "Almost done! Enter your email to see your top-rated matches.",
    type: "email_form",
    field: "email"
  }
}

const QUIZ_COOKIE_KEY = "rooferrate_quiz_done"
const LEGACY_QUIZ_COOKIE_KEY = "roofguide_quiz_done"
const TEASER_DISMISSED_KEY = "rooferrate_teaser_dismissed"
const LEGACY_TEASER_DISMISSED_KEY = "roofguide_teaser_dismissed"
const QUIZ_STEP_KEY = "rooferrate_quiz_step"
const LEGACY_QUIZ_STEP_KEY = "roofguide_quiz_step"
const QUIZ_ANSWERS_KEY = "rooferrate_quiz_answers"
const LEGACY_QUIZ_ANSWERS_KEY = "roofguide_quiz_answers"

export default class extends Controller {
  static targets = ["bubble", "teaser", "panel", "chatArea", "inputArea"]
  static values = { leadsUrl: String, turnstileKey: String, inline: Boolean }

  connect() {
    this.answers = {}
    this.currentStep = null
    this.isOpen = false
    this.turnstileToken = null

    // Restore from sessionStorage if available
    this.restoreProgress()

    // Auto-show teaser after 5 seconds on first visit
    if (!this.inlineValue && !this.quizDone && !this.teaserDismissed) {
      this.teaserTimeout = setTimeout(() => {
        if (!this.isOpen) {
          this.teaserTarget.classList.remove("hidden")
        }
      }, 5000)
    }

    // Pulse animation on bubble
    if (!this.inlineValue) {
      this.bubbleTarget.classList.add("animate-pulse")
      this.pulseTimeout = setTimeout(() => {
        this.bubbleTarget.classList.remove("animate-pulse")
      }, 3000)
    }
  }

  disconnect() {
    if (this.teaserTimeout) clearTimeout(this.teaserTimeout)
    if (this.pulseTimeout) clearTimeout(this.pulseTimeout)
  }

  get quizDone() {
    return this.cookiePresent(QUIZ_COOKIE_KEY) || this.cookiePresent(LEGACY_QUIZ_COOKIE_KEY)
  }

  get teaserDismissed() {
    return this.sessionValue(TEASER_DISMISSED_KEY, LEGACY_TEASER_DISMISSED_KEY) === "1"
  }

  toggle() {
    this.isOpen = !this.isOpen
    this.panelTarget.classList.toggle("hidden", !this.isOpen)
    this.teaserTarget.classList.add("hidden")

    if (this.isOpen && !this.currentStep) {
      const savedStep = this.sessionValue(QUIZ_STEP_KEY, LEGACY_QUIZ_STEP_KEY)
      const savedAnswers = this.sessionValue(QUIZ_ANSWERS_KEY, LEGACY_QUIZ_ANSWERS_KEY)
      if (savedStep && savedAnswers) {
        this.answers = JSON.parse(savedAnswers)
        this.showStep(savedStep)
      } else {
        this.startQuiz()
      }
    }
  }

  dismissTeaser() {
    this.teaserTarget.classList.add("hidden")
    sessionStorage.setItem(TEASER_DISMISSED_KEY, "1")
    sessionStorage.removeItem(LEGACY_TEASER_DISMISSED_KEY)
  }

  startQuiz() {
    this.showStep("start")
  }

  showStep(stepId) {
    const step = DECISION_TREE[stepId]
    if (!step) return

    this.currentStep = stepId
    this.saveProgress()

    // Add bot message with typing delay
    setTimeout(() => {
      this.addBotMessage(step.message)
      this.renderInput(step)
    }, 350)
  }

  addBotMessage(text) {
    const bubble = document.createElement("div")
    bubble.className = "flex justify-start"
    bubble.innerHTML = `
      <div class="max-w-[85%] bg-slate-100 text-slate-800 rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm">
        ${text}
      </div>
    `
    this.chatAreaTarget.appendChild(bubble)
    this.scrollToBottom()
  }

  addUserMessage(text) {
    const bubble = document.createElement("div")
    bubble.className = "flex justify-end"
    bubble.innerHTML = `
      <div class="max-w-[85%] bg-emerald-600 text-white rounded-2xl rounded-br-sm px-4 py-2.5 text-sm">
        ${text}
      </div>
    `
    this.chatAreaTarget.appendChild(bubble)
    this.scrollToBottom()
  }

  renderInput(step) {
    this.inputAreaTarget.innerHTML = ""

    if (step.type === "options") {
      const wrapper = document.createElement("div")
      wrapper.className = "flex flex-col gap-2"
      step.options.forEach(option => {
        const btn = document.createElement("button")
        btn.className = "w-full text-left px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg hover:bg-emerald-50 hover:border-emerald-300 transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600"
        btn.textContent = option.label
        btn.addEventListener("click", () => this.selectOption(option, step))
        wrapper.appendChild(btn)
      })
      this.inputAreaTarget.appendChild(wrapper)
    } else if (step.type === "text") {
      const form = document.createElement("form")
      form.className = "flex gap-2"
      form.innerHTML = `
        <input
          type="text"
          placeholder="${step.placeholder || ''}"
          class="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
          required
        />
        <button type="submit" class="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors cursor-pointer">
          Next
        </button>
      `
      form.addEventListener("submit", (e) => {
        e.preventDefault()
        const input = form.querySelector("input")
        const value = input.value.trim()
        if (value) {
          this.answers[step.field] = value
          this.addUserMessage(value)
          this.inputAreaTarget.innerHTML = ""
          this.saveProgress()
          this.showStep(step.next)
        }
      })
      this.inputAreaTarget.appendChild(form)
    } else if (step.type === "email_form") {
      const form = document.createElement("form")
      form.className = "flex flex-col gap-2"
      form.innerHTML = `
        <input
          type="email"
          placeholder="Email address"
          class="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
          required
        />
        <input
          type="tel"
          placeholder="Phone (optional)"
          class="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
        />
        <div id="quiz-turnstile-container"></div>
        <button type="submit" class="w-full px-4 py-2.5 min-h-[44px] bg-amber-500 text-white text-sm font-semibold rounded-lg hover:bg-amber-600 transition-colors cursor-pointer">
          Show My Matches
        </button>
      `
      form.addEventListener("submit", (e) => {
        e.preventDefault()
        const email = form.querySelector('input[type="email"]').value.trim()
        const phone = form.querySelector('input[type="tel"]').value.trim()
        if (email) {
          this.answers.email = email
          if (phone) this.answers.phone = phone
          this.submitLead()
        }
      })
      this.inputAreaTarget.appendChild(form)
      this.renderTurnstile()
    }
  }

  selectOption(option, step) {
    this.answers[step.field] = option.value
    this.addUserMessage(option.label)
    this.inputAreaTarget.innerHTML = ""
    this.saveProgress()
    this.showStep(option.next)
  }

  async submitLead() {
    const submitBtn = this.inputAreaTarget.querySelector('button[type="submit"]')
    if (submitBtn) {
      submitBtn.disabled = true
      submitBtn.textContent = "Finding matches..."
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(this.leadsUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        },
        body: JSON.stringify({
          lead: {
            ...this.answers,
            source_page: window.location.pathname
          },
          turnstile_token: this.turnstileToken || ""
        })
      })

      if (response.ok) {
        const data = await response.json()

        // Track lead capture
        if (typeof window.dataLayer !== 'undefined') {
          window.dataLayer.push({
            event: 'lead_captured',
            intent: this.answers.intent || '',
            roofing_type: this.answers.roofing_type || '',
            location: this.answers.location || '',
            urgency: this.answers.urgency || '',
            source_page: window.location.pathname
          })
        }

        // Mark quiz as done
        document.cookie = `${QUIZ_COOKIE_KEY}=1; max-age=2592000; path=/`
        document.cookie = `${LEGACY_QUIZ_COOKIE_KEY}=; max-age=0; path=/`
        this.clearProgress()

        // Show success message
        this.addBotMessage("Thanks! Here are your top-rated matches.")
        this.inputAreaTarget.innerHTML = `
          <a href="${data.redirect_url}" class="block w-full text-center px-4 py-2.5 min-h-[44px] bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition-colors leading-[44px]">
            View My Matches &rarr;
          </a>
        `
      } else {
        const data = await response.json()
        const errorMsg = data.errors?.join(", ") || "Something went wrong. Please try again."
        this.addBotMessage(errorMsg)
        if (submitBtn) {
          submitBtn.disabled = false
          submitBtn.textContent = "Show My Matches"
        }
      }
    } catch (error) {
      this.addBotMessage("Connection error. Please try again.")
      if (submitBtn) {
        submitBtn.disabled = false
        submitBtn.textContent = "Show My Matches"
      }
    }
  }

  renderTurnstile() {
    const container = document.getElementById("quiz-turnstile-container")
    if (!container) return

    const renderWidget = () => {
      if (window.turnstile) {
        this.turnstileWidgetId = window.turnstile.render(container, {
          sitekey: this.turnstileKeyValue,
          theme: "light",
          size: "compact",
          callback: (token) => { this.turnstileToken = token }
        })
      }
    }

    if (window.turnstile) {
      renderWidget()
    } else {
      const script = document.createElement("script")
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad&render=explicit"
      script.async = true
      window.onTurnstileLoad = renderWidget
      document.head.appendChild(script)
    }
  }

  scrollToBottom() {
    this.chatAreaTarget.scrollTop = this.chatAreaTarget.scrollHeight
  }

  saveProgress() {
    sessionStorage.setItem(QUIZ_STEP_KEY, this.currentStep)
    sessionStorage.setItem(QUIZ_ANSWERS_KEY, JSON.stringify(this.answers))
    sessionStorage.removeItem(LEGACY_QUIZ_STEP_KEY)
    sessionStorage.removeItem(LEGACY_QUIZ_ANSWERS_KEY)
  }

  restoreProgress() {
    const savedStep = this.sessionValue(QUIZ_STEP_KEY, LEGACY_QUIZ_STEP_KEY)
    const savedAnswers = this.sessionValue(QUIZ_ANSWERS_KEY, LEGACY_QUIZ_ANSWERS_KEY)

    if (savedStep && savedAnswers) {
      this.answers = JSON.parse(savedAnswers)
      // Don't auto-resume — just store it. When they open the panel, replay.
    }
  }

  clearProgress() {
    sessionStorage.removeItem(QUIZ_STEP_KEY)
    sessionStorage.removeItem(QUIZ_ANSWERS_KEY)
    sessionStorage.removeItem(LEGACY_QUIZ_STEP_KEY)
    sessionStorage.removeItem(LEGACY_QUIZ_ANSWERS_KEY)
  }

  sessionValue(key, legacyKey) {
    return sessionStorage.getItem(key) || sessionStorage.getItem(legacyKey)
  }

  cookiePresent(key) {
    return document.cookie.includes(`${key}=1`)
  }
}
